import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Phone, Mail, MapPin, MessageSquare, Send } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/customSupabaseClient';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Contact = () => {
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    const form = e.target;
    const formData = new FormData(form);
    const name = formData.get('name');
    const email = formData.get('email');
    const phone = formData.get('phone');
    const message = formData.get('message');

    const { data, error } = await supabase.functions.invoke('send-contact-email', {
      body: { name, email, phone, message },
    });

    if (error || (data && data.error)) {
      toast({
        variant: "destructive",
        title: "Error sending message",
        description: "There was a problem with your request. Please try again.",
      });
    } else {
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We will get back to you shortly.",
      });
      form.reset();
    }
    setIsSending(false);
  };

  return (
    <>
      <Helmet>
        <title>Contact & Reviews - Akiblackcar</title>
        <meta name="description" content="Get in touch with Akiblackcar for inquiries or support. We value your feedback and look forward to hearing from you." />
      </Helmet>
      <motion.div initial="hidden" animate="visible" variants={fadeIn} className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold font-serif text-brand-gold">Get In Touch</h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            We're here to help with any questions or special requests.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div variants={fadeIn}>
            <Card className="bg-brand-charcoal border-brand-gold/20 p-8 h-full">
              <CardHeader>
                <CardTitle className="text-3xl font-serif text-white flex items-center">
                  <MessageSquare className="mr-3 text-brand-gold" /> Contact Form
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-lg">Full Name</Label>
                    <Input id="name" name="name" placeholder="John Doe" required className="mt-2 h-12 text-lg" autoComplete="name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-lg">Email Address</Label>
                    <Input id="email" name="email" type="email" placeholder="john.doe@example.com" required className="mt-2 h-12 text-lg" autoComplete="email" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-lg">Phone Number</Label>
                    <Input id="phone" name="phone" type="tel" placeholder="(703) 843-1837" className="mt-2 h-12 text-lg" autoComplete="tel" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-lg">Message</Label>
                    <textarea id="message" name="message" rows="4" placeholder="Your message..." required className="w-full mt-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-lg text-gray-900 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" autoComplete="off"></textarea>
                  </div>
                  <Button type="submit" size="lg" className="w-full bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold text-xl py-7" disabled={isSending}>
                    {isSending ? 'Sending...' : 'Send Message'} <Send className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8">
            <Card className="bg-brand-charcoal border-brand-gold/20 p-8">
              <CardHeader>
                <CardTitle className="text-3xl font-serif text-white">Business Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-lg">
                <p className="flex items-center"><MapPin className="w-6 h-6 mr-4 text-brand-gold" /> Serving the DMV Area (DC, MD, VA)</p>
                <p className="flex items-center"><Phone className="w-6 h-6 mr-4 text-brand-gold" /> <a href="tel:7038431837" className="hover:text-brand-gold transition-colors">(703) 843-1837</a></p>
                <p className="flex items-center"><Mail className="w-6 h-6 mr-4 text-brand-gold" /> <a href="mailto:Akiblackcar@gmail.com" className="hover:text-brand-gold transition-colors">Akiblackcar@gmail.com</a></p>
              </CardContent>
            </Card>

            <Card className="bg-brand-charcoal border-brand-gold/20 p-8">
              <CardHeader>
                <CardTitle className="text-3xl font-serif text-white">Customer Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-300">
                  We pride ourselves on exceptional service. After your trip, we may reach out via email to request a review of your experience. Your feedback helps us maintain our high standards.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default Contact;